export interface MenuItem {
  text: string;
  link?: string;
  icon?: string;
  heading?: boolean;
  submenu?: MenuItem[];
  elink?: string;
  target?: string;
  alert?: string;
  label?: string;
  translate?: string;
  claim?: string;
  privilege?: string;
  state?: string;
  tierRestricted?: boolean; // If true, check tier visibility
}

export const MenuItems = {
  // Main navigation
  Home: {
    text: 'Home',
    link: '/home',
    icon: 'icon-home',
  },

  Dashboard: {
    text: 'Dashboard',
    link: '/dashboard/gettingstarted',
    icon: 'icon-speedometer',
    privilege: 'User',
  },

  Bots: {
    text: 'Bots',
    link: '/bots',
    icon: 'icon-bubbles',
    privilege: 'User',
  },

  Connectors: {
    text: 'Connectors',
    link: '/connectors',
    icon: 'icon-link',
    privilege: 'User',
    state: 'ready',
  },

  Users: {
    text: 'Admin Users',
    link: '/users',
    icon: 'icon-people',
    privilege: 'User',
  },

  ChatQueue: {
    text: 'Chat Queue',
    link: '/operator-chat/queue',
    icon: 'icon-earphones-alt',
    privilege: 'User',
  },

  Help: {
    text: 'Knowledge Base',
    elink: ':help_uri',
    target: 'blank',
    icon: 'icon-info',
  },

  Pages: {
    text: 'Pages',
    link: '/pages',
    icon: 'icon-doc',
    privilege: 'Developer',
    submenu: [
      {
        text: 'Lab',
        link: '/lab',
      },
    ],
  },

  // Bot-specific navigation
  Back: {
    text: 'Main Menu',
    link: '/bots',
    icon: 'icon-arrow-left',
  },

  BotAdmin: {
    text: 'Admin',
    link: '/bots/:id/configuration/admin',
    icon: 'icon-briefcase',
    privilege: 'SuperAdmin',
  },

  BotGeneral: {
    text: 'General',
    link: '/bots/:id/general',
    icon: 'icon-info',
    state: 'ready',
    privilege: 'Admin',
    tierRestricted: true,
  },

  BotDashboard: {
    text: 'Dashboard',
    link: '/bots/:id/launchpad',
    icon: 'icon-rocket',
  },

  BotChannels: {
    text: 'Channels',
    link: '/bots/:id/channels',
    icon: 'icon-globe',
    state: 'ready',
  },

  BotDialogs: {
    text: 'Dialogs',
    link: '/bots/:id/dialogs',
    icon: 'icon-speech',
    privilege: 'Dialog',
    tierRestricted: true,
  },

  BotDialogs2: {
    text: 'Dialog IDE',
    link: '/bots/:id/dialogs2',
    icon: 'icon-layers',
    privilege: 'Dialog',
    tierRestricted: true,
  },

  BotCommands: {
    text: 'Commands',
    link: '/bots/:id/commands',
    icon: 'icon-magic-wand',
    state: 'ready',
  },

  BotPlaybooks: {
    text: 'Playbooks',
    link: '/bots/:id/playbooks',
    icon: 'icon-book-open',
    state: 'ready',
    tierRestricted: true,
  },

  BotActions: {
    text: 'Actions',
    link: '/bots/:id/actions',
    icon: 'icon-target',
    privilege: 'Admin',
    state: 'ready',
    tierRestricted: true,
  },

  BotAudience: {
    text: 'Audience',
    link: '/bots/:id/audience',
    icon: 'icon-microphone',
    state: 'ready',
    privilege: 'Audience',
    tierRestricted: true,
  },

  BotConversations: {
    text: 'Conversations',
    link: '/bots/:id/conversations',
    icon: 'icon-bubbles',
    state: 'ready',
    privilege: 'SuperAdmin',
  },

  BotForms: {
    text: 'Forms',
    link: '/bots/:id/forms',
    icon: 'icon-list',
    privilege: 'Forms',
    state: 'ready',
    tierRestricted: true,
  },

  BotOperatorChat: {
    text: 'Operator Chat',
    link: '/bots/:id/operator-chat',
    icon: 'icon-earphones-alt',
    privilege: 'Operator',
    state: 'ready',
  },

  BotOrganization: {
    text: 'Account',
    link: '/organization',
    icon: 'icon-organization',
    state: 'ready',
  },

  BotQueries: {
    text: 'Queries',
    link: '/bots/:id/queries',
    icon: 'icon-speech',
    privilege: 'Queries',
    state: 'ready',
  },

  BotReports: {
    text: 'Chat Logs',
    link: '/bots/:id/reports',
    icon: 'icon-grid',
    state: 'ready',
  },

  BotResources: {
    text: 'Resources',
    link: '/bots/:id/resources',
    icon: 'icon-notebook',
    privilege: 'User',
    state: 'ready',
    tierRestricted: true,
  },

  BotDialogMessages: {
    text: 'Dialog Messages',
    link: '/bots/:id/dialog-messages',
    icon: 'icon-bubble',
    privilege: 'User',
    state: 'ready',
    tierRestricted: true,
  },

  BotRoles: {
    text: 'Roles',
    link: '/bots/:id/roles',
    icon: 'icon-key',
    privilege: 'Admin',
    tierRestricted: true,
  },

  BotTags: {
    text: 'Tags',
    link: '/bots/:id/tags',
    icon: 'icon-tag',
    privilege: 'Admin',
    tierRestricted: true,
  },

  BotVisuals: {
    text: 'Visuals',
    link: '/bots/:id/visuals',
    icon: 'icon-star',
    state: 'ready',
  },

  BotSettings: {
    text: 'Settings',
    link: '/bots/:id/settings',
    icon: 'icon-settings',
    state: 'ready',
  },

  BotDialogEvents: {
    text: 'Dialog Events',
    link: '/bots/:id/dialogevents',
    icon: 'icon-pencil',
    privilege: 'Developer',
  },

  BotEvents: {
    text: 'System Logs',
    link: '/bots/:id/events',
    icon: 'icon-clock',
  },

  BotTriggers: {
    text: 'Triggers',
    link: '/bots/:id/triggers',
    icon: 'icon-energy',
    privilege: 'Admin',
  },

  BotConnectors: {
    text: 'Connectors',
    link: '/bots/:id/connectors',
    icon: 'icon-link',
    privilege: 'Admin',
  },

  BotDialogOverrides: {
    text: 'Overrides',
    link: '/bots/:id/overrides',
    icon: 'icon-layers',
    privilege: 'SuperAdmin',
  },

  BotWatchers: {
    text: 'Watchers',
    link: '/bots/:id/watchers',
    icon: 'icon-eye',
    privilege: 'Developer',
    state: 'ready',
  },

  BotOrgUsers: {
    text: 'Admin Users',
    link: '/organization/users',
    icon: 'icon-user',
  },
};
